import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Filter, SearchService } from '@docpath/linces-angular-components/common';
import { DpHeaderModule, DpHeaderOption, DpSearchConfig, DpSearchModule, HeaderAction, MoreMenuGroup, SearchBasicInfo, SearchRowInfo } from '@docpath/webcomponents';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PageHeaderActionsService } from 'src/app/shared/components/header/services/page-header-actions.service';

import { Constants } from '../../../../constants/constants';
import { Job, JobStatus } from '../../models/job';
import { StatisticsService } from '../../services/statistics.service';
import { StatisticsTableService } from '../table/statistics-table.service';
import { StatisticsHeaderService } from './statistics-header.service';

@Component({
  selector: 'app-statistics-header',
  templateUrl: './statistics-header.component.html',
  standalone: true,
  imports: [DpHeaderModule, DpSearchModule]
})
export class StatisticsHeaderComponent implements OnInit, OnDestroy, OnChanges {

  i18nPrefix = 'statistics';

  @Input() jobs: Job[] = [];
  @Output() filtersChanged = new EventEmitter<Filter[]>();
  @Output() columnVisibilityChanged = new EventEmitter<DpHeaderOption>();

  title = `${this.i18nPrefix}.header.title`;
  rightActions: HeaderAction[] = [];
  leftActions: HeaderAction[] = [];
  moreMenuGroups: MoreMenuGroup[] = [];

  showSearch = false;
  searchConfig = new DpSearchConfig({ resetOnClose: true, showMinimizeButton: true });

  private filters: Filter[] = [];
  private jobChangedSubscription?: Subscription;
  private translateSubscription?: Subscription;

  dgeId?: number;
  numberHours?: number;
  customNumberHoursFrom?: string;
  customNumberHoursTo?: string;
  lastJobId?: number;

  searchPossibleFilters: SearchRowInfo[] = [];
  searchSelectedFilters: SearchRowInfo[] = [];
  searchAvailableOptions: SearchRowInfo[] = [];

  private readonly statisticsHeaderService = inject(StatisticsHeaderService);
  private readonly statisticsTableService = inject(StatisticsTableService);
  private readonly statisticsService = inject(StatisticsService);
  private readonly searchService = inject(SearchService);
  private readonly translateService = inject(TranslateService);
  private readonly pageHeaderActionsService = inject(PageHeaderActionsService);

  constructor() {
    this.searchAvailableOptions = this.statisticsHeaderService.initSearchAvailableOptions();
    this.rightActions = this.statisticsHeaderService.getRightActions();
    const searchAction = this.statisticsHeaderService.getSearchAction(this.rightActions);

    searchAction.action = () => {
      this.showSearch = !this.showSearch;
      this.updateSearchStyle();
    };
  }

  ngOnInit(): void {
    this.statisticsService.statisticsSelectsChanged.subscribe(statisticsFilter => {
      this.dgeId = statisticsFilter.dgeId;
      this.numberHours = statisticsFilter.numberHours;
      this.customNumberHoursFrom = statisticsFilter.customNumberHoursFrom;
      this.customNumberHoursTo = statisticsFilter.customNumberHoursTo;
      this.lastJobId = statisticsFilter.lastJobId;
    });

    if (!this.jobChangedSubscription) {
      this.jobChangedSubscription = this.statisticsService.tableChanged.subscribe(() => {
        this.leftActions = [];
        this.moreMenuGroups = [];
      });
    }

    this.statisticsService.statisticsTablePaginationChanged.subscribe(() => {
      this.leftActions = [];
      this.moreMenuGroups = [];
    });

    this.translateService.onLangChange.subscribe(() => {
      this.leftActions = [];
      this.moreMenuGroups = [];
    });

    if (!this.translateSubscription) {
      this.translateSubscription = this.translateService.onLangChange.subscribe(() => {
        this.updateSearchStyle();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.jobChangedSubscription) {
      this.jobChangedSubscription.unsubscribe();
    }

    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // eslint-disable-next-line dot-notation
    if (changes['jobs']) {
      this.leftActions = this.statisticsHeaderService.getLeftActions(this.jobs);
      const moreMenuGroupsActions = this.statisticsHeaderService.getMoreActions(this.jobs);
      this.moreMenuGroups = moreMenuGroupsActions.length > 0 ? [{ actions: moreMenuGroupsActions }] : [];
    }
  }

  isSearchVisible(): boolean {
    return this.showSearch;
  }

  clearSearch(): void {
    this.filters = [];
    this.filtersChanged.emit(this.filters);
    this.statisticsTableService.loadJobs(true, this.dgeId ?? Constants.DGE_ALL_VALUES, this.numberHours ?? Constants.NUMBER_OF_HOURS_24,
      this.customNumberHoursFrom ?? '', this.customNumberHoursTo ?? '', this.lastJobId ?? 0, this.filters, 1);
    this.leftActions = [];
    this.moreMenuGroups = [];
    this.updateSearchStyle(0);
  }


  sendSearch(currentFilters: Array<SearchBasicInfo>): void {
    this.filters = this.searchService.toFilters(currentFilters);
    this.filtersChanged.emit(this.filters);
    this.statisticsTableService.loadJobs(true, this.dgeId ?? Constants.DGE_ALL_VALUES, this.numberHours ?? Constants.NUMBER_OF_HOURS_24,
      this.customNumberHoursFrom ?? '', this.customNumberHoursTo ?? '', this.lastJobId ?? 0, this.filters, 1);
    this.leftActions = [];
    this.moreMenuGroups = [];
    this.updateSearchStyle();
  }

  hideSearch(): void {
    this.showSearch = false;
  }

  private updateSearchStyle(filtersCount?: number): void {
    const filterKeys = new Set(this.filters.map(filter => filter.key)),
      count = filtersCount ?? filterKeys.size;

    this.pageHeaderActionsService.updateSearchStyle(this.rightActions, count, 'search');
  }


  getStatusJobOptions(): Map<string, string> {
    return new Map<string, string>([
      [`${this.i18nPrefix}.table.status.option.OK`, JobStatus.OK],
      [`${this.i18nPrefix}.table.status.option.KO`, JobStatus.KO],
      [`${this.i18nPrefix}.table.status.option.warning`, JobStatus.warning]
    ]);
  }

  onChecklistOptionChanged(option: DpHeaderOption): void {
    const selectedFilter = this.searchSelectedFilters.find(filter => filter.key === option.value),
      possibleFilter = this.searchPossibleFilters.find(filter => filter.key === option.value);

    if (selectedFilter) {
      selectedFilter.visible = option.checked;
    }

    if (possibleFilter) {
      possibleFilter.visible = option.checked;
    }

    this.columnVisibilityChanged.emit(option);
  }


  onSelectedFiltersInitialized(filters: SearchRowInfo[]): void {
    this.searchSelectedFilters = filters;
  }

  onPossibleFiltersInitialized(filters: SearchRowInfo[]): void {
    this.searchPossibleFilters = filters;
  }


}
